import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  MapPin,
  Building2,
  Trees,
  Compass,
} from "lucide-react";
import DynamicImage from "@/components/common/DynamicImage";
import SEO from "@/components/common/SEO";
import { fadeUp } from "@/lib/motion";
import { useSettings } from "@/lib/SiteContent";
import { getPageSeo } from "@/lib/seo";

const Stat = ({ value, label }) => (
  <div className="flex flex-col items-start sm:items-center text-left sm:text-center px-6 py-5">
    <p className="font-display text-3xl md:text-4xl text-[color:var(--sp-gold)] [font-variant-numeric:tabular-nums]">
      {value}
    </p>
    <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-white/55">{label}</p>
  </div>
);

const HomePage = () => {
  const settings = useSettings();
  const contact = settings?.contact || {};
  const seo = getPageSeo("home");
  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100vh] flex items-center justify-start overflow-hidden"
      >
        <div className="absolute inset-0">
          <DynamicImage
            mediaKey="hero_home"
            alt="Luxury modern architecture under night sky"
            ratio=""
            className="h-full w-full"
            imgClassName="h-full w-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="container-narrow relative z-10 pt-24 pb-16">
          <motion.p
            {...fadeUp}
            className="text-[11px] sm:text-[12px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]"
          >
            <span className="gold-divider" />
            Seetai Properties Pvt. Ltd.
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-[-0.02em] mt-6 max-w-4xl"
          >
            Crafting Luxury.
            <br />
            <span className="italic text-white/90">Building </span>
            <span className="text-[color:var(--sp-gold)]">Legacy</span>
            <span className="text-[color:var(--sp-gold)]">.</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
          >
            Building a brighter future, one residence at a time. Discover premium
            real estate and visionary townships with Seetai Properties.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/projects"
              data-testid="hero-primary-cta-button"
              className="btn-gold neon-button neon-idle inline-flex items-center justify-center gap-2 px-7 py-4 text-[12px] tracking-[0.22em] uppercase"
            >
              Explore Silva City <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-secondary-cta-button"
              className="btn-ghost neon-button inline-flex items-center justify-center gap-2 px-7 py-4 text-[12px] tracking-[0.22em] uppercase"
            >
              Book a Site Visit
            </Link>
          </motion.div>

          {/* scroll indicator */}
          <div className="pointer-events-none absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/40">
            <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
            <span className="h-10 w-px bg-gradient-to-b from-[color:var(--sp-gold)] to-transparent" />
          </div>
        </div>
      </section>

      {/* COMPANY INTRO */}
      <section data-testid="home-intro" className="luxe-section bg-[#0a0a0a]">
        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" />
              Our Philosophy
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-5 leading-[1.1]">
              Where vision meets <span className="italic text-white/85">enduring craftsmanship.</span>
            </h2>
          </motion.div>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="lg:col-span-7 space-y-6 text-white/75 text-base md:text-lg leading-relaxed"
          >
            <p>
              Seetai Properties is a forward-thinking real estate developer dedicated
              to the design, construction, and stewardship of master-planned
              townships, premium NA plots, and commercial assets across Maharashtra.
            </p>
            <p>
              Every project is a study in restraint and detail — thoughtful land
              planning, considered architecture, and an obsessive focus on the
              lifestyle our patrons inherit. We don’t just deliver real estate;
              we deliver generational wealth.
            </p>
            <div className="flex flex-wrap gap-3 pt-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/75">
                <Building2 className="h-3.5 w-3.5 text-[color:var(--sp-gold)]" /> Master-Planned Townships
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/75">
                <Trees className="h-3.5 w-3.5 text-[color:var(--sp-emerald)]" /> Smart • Green Communities
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/75">
                <Compass className="h-3.5 w-3.5 text-[color:var(--sp-gold)]" /> Premium NA Plotting
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PROJECT — SILVA CITY */}
      <section
        data-testid="home-featured-project"
        className="luxe-section relative bg-[#0a0a0a] border-y border-white/5"
      >
        <div className="container-narrow grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" />
              Flagship Project
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[1.05]">
              Silva City.
            </h2>
            <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed">
              <span className="text-[color:var(--sp-gold)] font-medium">Silva City = Dreams.</span> Your
              paradise, soon to be revealed. A visionary pre-planned premium
              plotting township nestled at the convergence of nature and rapid
              infrastructure growth.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-4">
              <div className="glass-card p-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Plot Sizes</p>
                <p className="mt-2 font-display text-2xl text-white">1,200 – 5,000 sq.ft.</p>
              </div>
              <div className="glass-card p-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Location</p>
                <p className="mt-2 font-display text-2xl text-white">Khor, Bhandgaon</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/projects"
                data-testid="featured-project-cta"
                className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-[12px] tracking-[0.2em] uppercase"
              >
                Discover Silva City <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${contact.whatsappDigits || "919637241414"}`}
                className="btn-ghost neon-button inline-flex items-center px-6 py-3 text-[12px] tracking-[0.2em] uppercase"
              >
                Speak to a Consultant
              </a>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[20px] bg-gradient-to-tr from-[color:rgba(212,175,55,0.18)] to-transparent blur-2xl" />
            <DynamicImage
              mediaKey="featured_project"
              alt="Aerial view of luxury master-planned township"
              ratio="aspect-[4/5]"
              className="rounded-[20px] border border-white/10 neon-border shadow-[var(--sp-shadow)]"
            />
            <div className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md border border-[color:rgba(212,175,55,0.4)] px-4 py-2 text-[11px] tracking-[0.22em] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--sp-gold)] animate-pulse" />
              Now Reserving Plots
            </div>
            <div className="absolute bottom-6 right-6 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 px-5 py-3 text-right">
              <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Approved</p>
              <p className="mt-1 font-display text-sm text-white">Town Planning Approved NA Plots</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="home-why-choose-us" className="luxe-section bg-[#0a0a0a]">
        <div className="container-narrow">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" />
              Why Choose Seetai
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              Three pillars of <span className="italic text-white/85">enduring value.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              {
                icon: MapPin,
                title: "Visionary Town Planning",
                desc: "Master-planned communities designed by leading urban architects, with infrastructure built for the next 50 years.",
              },
              {
                icon: ShieldCheck,
                title: "Uncompromised Quality",
                desc: "Every plot, road, and amenity is delivered with institutional-grade discipline and transparent legal title.",
                accentEmerald: true,
              },
              {
                icon: TrendingUp,
                title: "High ROI Asset Management",
                desc: "Strategic locations, smart pricing, and post-sale property management built to maximize generational wealth.",
              },
            ].map(({ icon: Icon, title, desc, accentEmerald }, i) => (
              <motion.div
                key={title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                className="glass-card neon-border p-8 card-lift relative"
              >
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full border ${
                    accentEmerald
                      ? "border-[color:rgba(4,99,7,0.45)] bg-[color:var(--sp-emerald-soft)]"
                      : "border-[color:rgba(212,175,55,0.45)] bg-[color:var(--sp-gold-soft)]"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      accentEmerald
                        ? "text-[color:var(--sp-emerald)]"
                        : "text-[color:var(--sp-gold)]"
                    }`}
                  />
                </div>
                <h3 className="font-display text-2xl mt-6">{title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed text-[15px]">{desc}</p>
                <div className="mt-8 flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-white/45">
                  <span>0{i + 1}</span>
                  <Sparkles className="h-3.5 w-3.5 text-[color:var(--sp-gold)]" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="container-narrow">
          <motion.div
            {...fadeUp}
            className="glass-strong rounded-[var(--sp-radius-lg)] grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/8 overflow-hidden"
          >
            <Stat value="100+" label="Acres Master-Planned" />
            <Stat value="1,200+" label="Premium Plots" />
            <Stat value="6" label="World-Class Amenities" />
            <Stat value="24/7" label="On-Site Security & Power" />
          </motion.div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section data-testid="home-closing-cta" className="luxe-section bg-[#0a0a0a]">
        <div className="container-narrow">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-[#121212] to-[#0a0a0a] p-10 md:p-16"
          >
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[color:rgba(212,175,55,0.15)] blur-3xl" />
            <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[color:rgba(4,99,7,0.12)] blur-3xl" />
            <div className="relative grid lg:grid-cols-2 items-center gap-10">
              <div>
                <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                  <span className="gold-divider" />
                  Reserve Your Plot
                </p>
                <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.08]">
                  Begin a conversation that shapes your legacy.
                </h2>
              </div>
              <div className="flex flex-col items-start lg:items-end gap-4">
                <p className="text-white/65 max-w-md lg:text-right">
                  Personalised site visits, private investor briefings, and bespoke
                  plot consultations — by invitation.
                </p>
                <div className="flex gap-3">
                  <Link
                    to="/contact"
                    data-testid="closing-cta-primary"
                    className="btn-gold inline-flex items-center px-6 py-3 text-[12px] tracking-[0.22em] uppercase"
                  >
                    Request a Visit
                  </Link>
                  <Link
                    to="/projects"
                    className="btn-ghost inline-flex items-center px-6 py-3 text-[12px] tracking-[0.22em] uppercase"
                  >
                    View Projects
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
